"use client";

import { useEffect } from "react";

/** 全局崩溃上报。
 *
 *  原来只有 app/chat/error.tsx 一个上报点，`/chat` 之外崩了没人收；
 *  而且它拿不到 React 的**组件栈**——恰恰是定位「Maximum update depth
 *  exceeded」(#185) 唯一有用的东西：那类错误的 JS 调用栈全在 React 内部，
 *  只有组件栈能指出是哪个组件在反复 setState。
 *
 *  React 把组件栈用 console.error 单独打出来（"The above error occurred
 *  in the <X> component"），不走 Error 对象，所以必须挂 console.error 才拿得到。
 */

const MAX_REPORTS = 5; // 无限循环会刷屏，别把上报本身变成 DDoS
let sent = 0;
const seen = new Set<string>();
let lastConsoleErrors: string[] = [];

function report(payload: Record<string, unknown>) {
  const key = String(payload.message ?? "") + String(payload.stack ?? "").slice(0, 200);
  if (seen.has(key) || sent >= MAX_REPORTS) return;
  seen.add(key);
  sent += 1;
  void fetch("/api/client-error", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...payload,
      url: window.location.href,
      ua: navigator.userAgent,
      // React 的组件栈在这里面
      consoleErrors: lastConsoleErrors.slice(-4).join("\n---\n").slice(0, 4000)
    })
  }).catch(() => {
    /* 上报失败就算了，绝不能变成新的错误源 */
  });
}

/** 主动上报口。给那些**被别人接住、永远到不了 console.error 的错误**用——
 *  典型是 AI SDK 的 onError：它在自己的 try/catch 里吃掉异常转成回调，
 *  React 从没打印过，劫持 console.error 的钩子一次都不会响。
 *
 *  `context` 放现场状态快照。对 #185（Maximum update depth exceeded）来说
 *  它比调用栈有用：那类错误的 JS 栈全在 React 内部，而"当时有多少条消息、
 *  status 是什么、是不是刚切过会话"才指得出是哪个循环。 */
export function reportClientError(
  kind: string,
  err: unknown,
  context?: Record<string, unknown>
) {
  report({
    kind,
    message: err instanceof Error ? err.message : String(err),
    stack: err instanceof Error ? (err.stack ?? "") : "",
    context: context ? JSON.stringify(context).slice(0, 1500) : undefined
  });
}

/** 给 error.tsx 用：React 错误边界抓到的错误**不会**触发 window.onerror，
 *  所以那条上报路径得自己来取组件栈缓冲。 */
export function getRecentConsoleErrors(): string {
  return lastConsoleErrors.slice(-4).join("\n---\n").slice(0, 4000);
}

/** 只匹配 React 真正的**错误**，不匹配 Warning。
 *
 *  为什么要窄：report() 有 MAX_REPORTS=5 的硬额度，见 console.error 就报的话
 *  额度会被无害噪音（图片 404、第三方脚本、key 警告）吃光，真正的崩溃反而挤不进来。
 *  上报器"覆盖越全"在这里等于"什么都收不到"。
 */
const REACT_ERROR_PATTERNS = [
  /Minified React error #\d+/,           // 生产构建的所有 React 错误，含 #185
  /Maximum update depth exceeded/,       // 开发构建下 #185 的原文
  /The above error occurred in/,          // 组件栈那一行（错误边界也走它）
  /Rendered (more|fewer) hooks than/,     // hooks 数量变化，另一类必崩
  /Should have a queue/,                  // hooks 状态错乱
];

function isReactError(line: string): boolean {
  return REACT_ERROR_PATTERNS.some((re) => re.test(line));
}

export function ErrorReporter() {
  useEffect(() => {
    const origConsoleError = console.error;
    // 只缓冲，不改变原行为——开发时该看到的照样看到
    console.error = (...args: unknown[]) => {
      try {
        const line = args
          .map((a) => (a instanceof Error ? `${a.message}\n${a.stack ?? ""}` : String(a)))
          .join(" ");
        lastConsoleErrors.push(line.slice(0, 3000));
        if (lastConsoleErrors.length > 8) lastConsoleErrors = lastConsoleErrors.slice(-8);
        if (isReactError(line)) {
          // 延一个 macrotask 再报：React 把**组件栈**放在紧随其后的另一次
          // console.error 里（"The above error occurred in the <X> component"），
          // 立刻上报会把它落下，而那正是定位 #185 唯一有用的东西。
          const captured = line.slice(0, 1000);
          setTimeout(() => {
            report({ kind: "react-console", message: captured, stack: "" });
          }, 0);
        }
      } catch {
        /* 缓冲失败不影响打印 */
      }
      origConsoleError(...args);
    };

    const onError = (e: ErrorEvent) => {
      report({
        kind: "window.onerror",
        message: e.message,
        stack: e.error instanceof Error ? e.error.stack : `${e.filename}:${e.lineno}:${e.colno}`
      });
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const r = e.reason;
      report({
        kind: "unhandledrejection",
        message: r instanceof Error ? r.message : String(r),
        stack: r instanceof Error ? r.stack : ""
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      console.error = origConsoleError;
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
